import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { API_URLS } from "../App";
import api from "../hooks/api";
import { useLayoutContext } from "../context/LayoutContext";
import { Category } from "../model/Category";
import ManageCategory from "../components/Categories/ManageCategory";
import ListCategoryItems from "../components/Categories/ListCategoryItems";
import { reportError } from "../helpers/reportError";
import { normalizeCategories } from "../lib/categories/normalizeCategories";

type FetchResult = "ok" | "stale" | "error";

const isCanceled = (error: any): boolean =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const CATEGORIES_TIMEOUT_MS = 15000;

const findCategoryById = (
  categories: Category[],
  id: number,
): Category | undefined => {
  for (const category of categories) {
    if (category.id === id) return category;
    const child = category.children
      ? findCategoryById(category.children, id)
      : undefined;
    if (child) return child;
  }
  return undefined;
};

const countDescendants = (category: Category): number =>
  (category.children ?? []).reduce(
    (total, child) => total + 1 + countDescendants(child),
    0,
  );

const CategoriesManagementPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setModalScreen, setError, setSuccess } = useLayoutContext();

  const [categories, setCategories] = useState<Category[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(() => new Set());
  // Ref mirror of the modal key derived from the URL. It prevents the effect
  // below from reopening the same modal whenever `categories` or the callbacks
  // change identity; only a new URL-derived key opens a new modal.
  const openModalKey = useRef<string | null>(null);
  const openModalToken = useRef<object | null>(null);
  const requestId = useRef(0);
  const mountedRef = useRef(true);
  const deletingIdsRef = useRef<Set<number>>(new Set());
  const activeFetchRef = useRef<AbortController | null>(null);
  const activeDeleteRef = useRef<AbortController | null>(null);

  const fetchCategories = useCallback(async (): Promise<FetchResult> => {
    activeFetchRef.current?.abort();
    const controller = new AbortController();
    activeFetchRef.current = controller;

    const request = ++requestId.current;
    try {
      const response = await api.get(API_URLS.CATEGORIES, {
        timeout: CATEGORIES_TIMEOUT_MS,
        signal: controller.signal,
      });
      if (request !== requestId.current) return "stale";

      const normalized = normalizeCategories(response.data);
      if (!normalized.valid) {
        reportError(
          "categories.list",
          new Error("Resposta inválida do servidor"),
        );
        setLoadError("Resposta inválida do servidor");
        return "error";
      }
      if (normalized.dropped > 0) {
        reportError("categories.normalize", new Error("Categorias inválidas"), {
          dropped: normalized.dropped,
        });
      }
      if (normalized.categories.length === 0 && normalized.dropped > 0) {
        setLoadError("Resposta inválida do servidor");
        return "error";
      }

      setCategories(normalized.categories);
      setLoadError(
        normalized.dropped > 0
          ? `Alguns registos não puderam ser apresentados (${normalized.dropped} ignorado${normalized.dropped === 1 ? "" : "s"}).`
          : null,
      );
      return "ok";
    } catch (error: any) {
      if (isCanceled(error)) return "stale";
      if (request !== requestId.current) return "stale";
      reportError("categories.list", error, {
        "http.status": error.response?.status,
      });
      setLoadError("Erro ao carregar categorias");
      return "error";
    } finally {
      if (activeFetchRef.current === controller) {
        activeFetchRef.current = null;
      }
      if (request === requestId.current) setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
      return;
    }
    void fetchCategories();

    return () => {
      // The modal lives in LayoutContext (above the router), so it must be
      // closed explicitly when this page unmounts. The request token makes
      // any in-flight fetch a no-op.
      mountedRef.current = false;
      requestId.current += 1;
      activeFetchRef.current?.abort();
      activeFetchRef.current = null;
      activeDeleteRef.current?.abort();
      activeDeleteRef.current = null;
      openModalKey.current = null;
      openModalToken.current = null;
      setModalScreen(null);
    };
  }, [isAuthenticated, navigate, pathname, fetchCategories, setModalScreen]);

  const clearModalParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeModal = useCallback(() => {
    openModalKey.current = null;
    openModalToken.current = null;
    setModalScreen(null);
    clearModalParams();
  }, [clearModalParams, setModalScreen]);

  const handleCategoryUpdated = useCallback(
    async (updatedCategory: Category, token: object) => {
      if (!mountedRef.current) return;

      // Only close the modal instance that initiated this save. If the operator
      // moved to another category (or the create form) meanwhile, leave it open.
      if (openModalToken.current === token) {
        closeModal();
      }

      const result = await fetchCategories();
      if (!mountedRef.current) return;

      if (result === "error") {
        setError(
          `Categoria "${updatedCategory.name}" guardada, mas a lista não foi atualizada. Recarregue a página.`,
        );
      } else {
        setSuccess(`Categoria "${updatedCategory.name}" guardada com sucesso!`);
      }
    },
    [closeModal, fetchCategories, setError, setSuccess],
  );

  const handleDeleteCategory = useCallback(
    async (category: Category) => {
      if (category.id == null || deletingIdsRef.current.has(category.id)) {
        return;
      }

      deletingIdsRef.current.add(category.id);
      setDeletingIds(new Set(deletingIdsRef.current));

      const controller = new AbortController();
      activeDeleteRef.current = controller;

      try {
        await api.delete(`${API_URLS.CATEGORIES}/${category.id}`, {
          timeout: 20000,
          signal: controller.signal,
        });
        if (!mountedRef.current) return;
        const result = await fetchCategories();
        if (!mountedRef.current) return;

        if (result === "error") {
          setError(
            `Categoria "${category.name}" apagada, mas a lista não foi atualizada. Recarregue a página.`,
          );
        } else {
          setSuccess(`Categoria "${category.name}" apagada com sucesso!`);
        }
      } catch (error: any) {
        if (isCanceled(error)) return;
        if (!mountedRef.current) return;
        reportError("categories.delete", error, {
          "http.status": error.response?.status,
          "category.id": category.id,
        });
        setError(error.response?.data?.message || "Erro ao apagar categoria");
      } finally {
        if (activeDeleteRef.current === controller) {
          activeDeleteRef.current = null;
        }
        if (category.id != null) {
          deletingIdsRef.current.delete(category.id);
          if (mountedRef.current) {
            setDeletingIds(new Set(deletingIdsRef.current));
          }
        }
      }
    },
    [fetchCategories, setError, setSuccess],
  );

  const confirmDeleteCategory = useCallback(
    (category: Category) => {
      const descendants = countDescendants(category);
      const blocked = descendants > 0;

      setModalScreen({
        state: blocked ? "alert" : "confirm",
        title: "Apagar categoria",
        dismissOnEscape: true,
        children: (
          <div className="space-y-4 p-6">
            {blocked ? (
              <p className="text-sm text-gray-700">
                A categoria <strong>{category.name}</strong> tem {descendants}{" "}
                subcategoria{descendants === 1 ? "" : "s"}. Apague ou mova-as
                primeiro — a base de dados não apaga os filhos em cascata.
              </p>
            ) : (
              <p className="text-sm text-gray-700">
                Tem a certeza que deseja apagar a categoria{" "}
                <strong>{category.name}</strong>? Esta ação não pode ser
                revertida.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalScreen(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                {blocked ? "Fechar" : "Cancelar"}
              </button>
              {!blocked && (
                <button
                  type="button"
                  onClick={() => {
                    setModalScreen(null);
                    void handleDeleteCategory(category);
                  }}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition"
                >
                  Apagar
                </button>
              )}
            </div>
          </div>
        ),
        onClose: () => setModalScreen(null),
      });
    },
    [handleDeleteCategory, setModalScreen],
  );

  const openManageModal = useCallback(
    (category?: Category) => {
      const token = {};
      openModalToken.current = token;

      setModalScreen({
        state: "edit",
        title: category
          ? `Editar Categoria: ${category.name}`
          : "Criar Nova Categoria",
        children: (
          <ManageCategory
            key={category?.id ?? "new"}
            category={category}
            onCategoryUpdate={(updatedCategory) =>
              handleCategoryUpdated(updatedCategory, token)
            }
            setError={setError}
            setSuccess={setSuccess}
            categories={categories}
          />
        ),
        onClose: closeModal,
      });
    },
    [
      categories,
      closeModal,
      handleCategoryUpdated,
      setError,
      setModalScreen,
      setSuccess,
    ],
  );

  const editParam = searchParams.get("edit");
  const isNew = searchParams.get("new") === "1";

  useEffect(() => {
    if (!isAuthenticated() || !hasLoaded) return;

    // Normalise hand-crafted URLs before opening anything.
    if (isNew && editParam !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!isNew && editParam === "") {
      clearModalParams();
      return;
    }

    const desiredKey = isNew ? "new" : editParam ? `edit:${editParam}` : null;

    if (!desiredKey) {
      if (openModalKey.current) {
        openModalKey.current = null;
        openModalToken.current = null;
        setModalScreen(null);
      }
      return;
    }

    if (openModalKey.current === desiredKey) return;

    const failedLoad = !!loadError && categories.length === 0;
    if (failedLoad) return;

    if (isNew) {
      openModalKey.current = desiredKey;
      openManageModal();
      return;
    }

    const editId = Number(editParam);
    const validEditId =
      /^\d+$/.test(editParam ?? "") && Number.isSafeInteger(editId);

    if (!validEditId) {
      openModalKey.current = null;
      openModalToken.current = null;
      setError("Categoria não encontrada");
      clearModalParams();
      return;
    }

    const found = findCategoryById(categories, editId);
    if (!found) {
      openModalKey.current = null;
      openModalToken.current = null;
      setError("Categoria não encontrada");
      clearModalParams();
      return;
    }

    openModalKey.current = desiredKey;
    openManageModal(found);
  }, [
    editParam,
    isNew,
    categories,
    hasLoaded,
    loadError,
    isAuthenticated,
    openManageModal,
    setModalScreen,
    setError,
    clearModalParams,
    searchParams,
    setSearchParams,
  ]);

  const openCreateCategory = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    next.set("new", "1");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const openEditCategory = useCallback(
    (category: Category) => {
      if (category.id == null) return;
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      next.set("edit", String(category.id));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const retryLoad = useCallback(() => {
    if (categories.length === 0) {
      setHasLoaded(false);
    }
    setLoadError(null);
    void fetchCategories();
  }, [categories.length, fetchCategories]);

  if (!isAuthenticated()) return null;

  const showLoadError = hasLoaded && !!loadError && categories.length === 0;
  const showRefreshWarning = hasLoaded && !!loadError && categories.length > 0;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              Categorias
            </h1>
            <p className="text-gray-500 mt-1">
              Gerencie a estrutura de categorias da loja.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateCategory}
            disabled={!hasLoaded || showLoadError}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Nova Categoria
          </button>
        </div>

        {showRefreshWarning && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <span className="text-sm">{loadError}</span>
            <button
              type="button"
              onClick={retryLoad}
              className="px-3 py-1.5 text-sm rounded-md border border-amber-300 bg-white hover:bg-amber-100 transition"
            >
              Tentar novamente
            </button>
          </div>
        )}

        <div className="overflow-auto">
          {!hasLoaded ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">A carregar categorias...</p>
              </div>
            </div>
          ) : showLoadError ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-700 font-medium">
                Não foi possível carregar as categorias.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Verifique a ligação e tente novamente.
              </p>
              <button
                type="button"
                onClick={retryLoad}
                className="mt-4 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <ListCategoryItems
              data={categories}
              handleManageItem={openEditCategory}
              handleDeleteItem={confirmDeleteCategory}
              deletingIds={deletingIds}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagementPage;
